import React, { ReactNode, createContext, useContext, useId } from 'react'
import styles from './FieldWrapper.module.css'
import { SortDirection, SortIcon } from '../../icons/SortIcon/SortIcon'

/**
 * The id the label points at, handed down to whichever control renders inside.
 *
 * Association by context rather than by an `htmlFor`/`id` pair written out at every call site:
 * there are ~60 fields across the two upsert forms alone, and none of them had a real `<label>` at
 * all — the label was a `<p>`, so every input in the app was anonymous to a screen reader. Doing it
 * here fixes all of them at once and leaves the call sites untouched.
 *
 * Only the form-bound controls consume it (`FormInput`, `FormSelect`, `EntitySearchSelect`), so a
 * wrapper that also contains something self-labelling — the Ganztägig `Checkbox` sitting under
 * "Termin um" — does not end up with two elements claiming the same id.
 */
interface FieldContextValue {
  id: string
  labelId: string
  required: boolean
}

const FieldContext = createContext<FieldContextValue | undefined>(undefined)

export const useFieldId = () => useContext(FieldContext)?.id

/**
 * Id of the `<label>` itself, for controls that `htmlFor` cannot reach.
 *
 * `htmlFor` only binds to *labelable* elements — the native inputs. `Select` and
 * `MultiSelectDropdown` are `role="combobox"` on a `<div>`, so the label resolves to nothing and
 * they stay anonymous however correct the `id` is. Those point back with `aria-labelledby`.
 */
export const useFieldLabelId = () => useContext(FieldContext)?.labelId

/** So a control can set `aria-required` from the same place the `*` is decided. */
export const useFieldRequired = () => useContext(FieldContext)?.required ?? false

interface Props {
  label: string
  children?: ReactNode
  className?: string
  topChildren?: ReactNode
  error?: string
  /**
   * Marks the field required — renders the `*` and sets `aria-required` on the control.
   *
   * Prefer this to writing the asterisk into `label`: the two drifted apart, and "Anfahrt (min)*"
   * was carrying one while its schema accepted an empty value.
   */
  required?: boolean

  sortKey?: string
  activeSortField?: string
  sortOrder?: SortDirection
  onSortChange?: (field: string, direction: SortDirection) => void
}

export const FieldWrapper: React.FC<Props> = ({
  label,
  className,
  topChildren,
  children,
  error,
  required,
  sortKey,
  activeSortField,
  sortOrder,
  onSortChange,
}) => {
  const fieldId = useId()
  const labelId = `${fieldId}-label`
  const fieldContext = React.useMemo(
    () => ({ id: fieldId, labelId, required: Boolean(required) }),
    [fieldId, labelId, required],
  )
  const isSortable = Boolean(sortKey && onSortChange)
  const isActive = isSortable && activeSortField === sortKey

  const handleClick = () => {
    if (!isSortable || !sortKey) return

    const nextDirection: SortDirection =
      isActive && sortOrder === 'ASC' ? 'DESC' : 'ASC'

    onSortChange?.(sortKey, nextDirection)
  }

  // A sortable header is a column control, not a field label — pointing an `htmlFor` at nothing
  // would be worse than the `<p>` it has always been.
  const Title = isSortable ? 'p' : 'label'

  return (
    <div className={`${styles.wrapper} ${className ?? ''}`}>
      <div
        className={styles.header}
        onClick={isSortable ? handleClick : undefined}
      >
        <Title
          id={labelId}
          className={`${styles.title} ${error ? styles.titleError : ''}`}
          htmlFor={isSortable ? undefined : fieldId}
        >
          {label}
          {/*
            Visual cue only. The announcement comes from `aria-required` on the control itself —
            spelling it out here as well would both read the word twice and collide with the
            validation message, which is also "Pflichtfeld".
          */}
          {required && <span aria-hidden="true">*</span>}
        </Title>

        {topChildren}

        {isSortable && (
          <SortIcon
            active={isActive}
            direction={isActive ? sortOrder : undefined}
          />
        )}
      </div>

      <FieldContext.Provider value={fieldContext}>{children}</FieldContext.Provider>
    </div>
  )
}
